import React, { createContext, useContext, useState, ReactNode } from "react";
import { BACKEND_URL } from "../constants/urls.const";

type ComparisonState = {
  firstText: string;
  setFirstText: (v: string) => void;
  secondText: string;
  setSecondText: (v: string) => void;
  loading: boolean;
  result: any;
  error: string;
  jobId: string | null;
  handleClear: () => void;
  handleSubmit: () => Promise<void>;
};

const ComparisonContext = createContext<ComparisonState | undefined>(undefined);

export const ComparisonProvider = ({ children }: { children: ReactNode }) => {
  const [firstText, setFirstText] = useState("");
  const [secondText, setSecondText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState("");
  const [jobId, setJobId] = useState<string | null>(null);

  // Polling function to get job status
  const pollJobStatus = async (id: string) => {
    try {
      const response = await fetch(`${BACKEND_URL}/alignment/${id}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      const allDone = data.status === "done";

      setResult(data);

      if (allDone) {
        // All workers finished — stop loading
        setLoading(false);
      } else {
        // keep polling until allDone
        setTimeout(() => pollJobStatus(id), 1000);
      }
    } catch (err) {
      console.error(err);
      setError(
        err instanceof Error ? err.message : "Error fetching job status",
      );
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFirstText("");
    setSecondText("");
    setLoading(false);
    setResult(null);
  };

  const handleSubmit = async () => {
    // mark loading and clear previous state
    setLoading(true);
    setError("");
    setResult(null);
    setJobId(null);

    try {
      const payload = {
        "text1": "",
        "text2": ""
      }

      if (firstText && secondText) {
        payload.text1 = firstText;
        payload.text2 = secondText;
      } else {
        throw new Error("Please provide both texts");
      }

      const response = await fetch(`${BACKEND_URL}/alignment/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setJobId(data.job_id);

      // start polling — loading will be cleared by pollJobStatus once allDone
      pollJobStatus(data.job_id);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : "An error occurred");
      setLoading(false);
    }
  };

  return (
    <ComparisonContext.Provider
      value={{
        firstText,
        setFirstText,
        secondText,
        setSecondText,
        loading,
        result,
        error,
        jobId,
        handleClear,
        handleSubmit,
      }}
    >
      {children}
    </ComparisonContext.Provider>
  );
};

export const useComparison = () => {
  const ctx = useContext(ComparisonContext);
  if (!ctx) throw new Error("useComparison must be used within ComparisonProvider");
  return ctx;
};

export default ComparisonContext;
